import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { TC } from "../tokens/text";

// Dialog paper styles are applied theme-aware at render so light/dark modes behave correctly.

/*Bearer ${token}`
 * Blocks all navigation (sidebar links, browser back, tab close) when isDirty.
 * Shows a fully themed confirmation dialog instead of the browser native confirm.
 *
 * Usage:
 *   const { ConfirmDialog, guardClose } = useUnsavedChanges(isDirty);
 *   // In JSX: render {ConfirmDialog} anywhere in the tree
 *   // For modal cancel buttons: onClick={() => guardClose(onClose)}
 */
export function useUnsavedChanges(isDirty) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const pendingRef    = useRef(null);   // { type: "navigate"|"callback", location?, fn? }
  const unblockRef    = useRef(null);
  const skipNextBlock = useRef(false);

  // ── Block in-app navigation ───────────────────────────────────────────────
  useEffect(() => {
    if (!isDirty) {
      unblockRef.current?.();
      unblockRef.current = null;
      return;
    }

    unblockRef.current = history.block((location) => {
      if (skipNextBlock.current) {
        skipNextBlock.current = false;
        return undefined; // allow this one navigation through
      }
      pendingRef.current = { type: "navigate", location };
      setOpen(true);
      return false; // silently cancel — we'll replay it after confirm
    });

    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [isDirty, history]);

  // ── Block browser close / refresh ─────────────────────────────────────────
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // ── guardClose — for modal cancel/X buttons ───────────────────────────────
  const guardClose = useCallback((closeFn) => {
    if (!isDirty) { closeFn(); return; }
    pendingRef.current = { type: "callback", fn: closeFn };
    setOpen(true);
  }, [isDirty]);

  // ── Confirm: leave without saving ─────────────────────────────────────────
  const handleLeave = () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    if (!pending) return;

    if (pending.type === "navigate") {
      skipNextBlock.current = true;
      history.push(pending.location);
    } else if (pending.type === "callback") {
      pending.fn();
    }
  };

  // ── Cancel: stay and keep editing ─────────────────────────────────────────
  const handleStay = () => {
    pendingRef.current = null;
    setOpen(false);
  };

  // ── Themed dialog JSX (consumer renders this in their tree) ───────────────
  const ConfirmDialog = (
    <Dialog
      open={open}
      onClose={handleStay}
      sx={(theme) => ({ zIndex: theme.zIndex.modal + 300 })}
      PaperProps={{ sx: (theme) => ({
        backgroundImage: "none",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "16px",
        boxShadow: "0 8px 40px rgba(0,87,216,0.12), 0 2px 8px rgba(0,0,0,0.06)",
        minWidth: 360,
        maxWidth: 460,
        color: theme.palette.text.primary,
          zIndex: theme.zIndex.modal + 200,
        }) }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <VuiBox
            sx={{
              background: "rgba(245,124,0,0.08)",
              border: "1px solid rgba(245,124,0,0.25)",
              borderRadius: "10px",
              p: 0.8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: "#f57c00", fontSize: "1.8rem" }} />
          </VuiBox>
          <VuiTypography variant="h5" fontWeight="bold" sx={{ letterSpacing: "0.01em", color: (t) => t.palette.text.primary }}>
            Unsaved Changes
          </VuiTypography>
        </VuiBox>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 1, px: 3 }}>
        <VuiBox
          sx={{
            background: "rgba(245,124,0,0.05)",
            border: "1px solid rgba(245,124,0,0.18)",
            borderRadius: "12px",
            px: 2.5, py: 2,
          }}
        >
          <VuiTypography variant="body2" sx={{ lineHeight: 1.7, fontSize: "0.95rem", color: (t) => t.palette.text.primary }}>
            You have{" "}
            <strong style={{ color: "#f57c00" }}>unsaved changes</strong>{" "}
            that will be permanently lost if you leave.
          </VuiTypography>
          <VuiTypography variant="body2" sx={{ mt: 1, fontSize: "0.85rem", color: (t) => t.palette.text.secondary }}>
            Do you want to continue editing, or discard your changes and leave?
          </VuiTypography>
        </VuiBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5, justifyContent: "flex-end" }}>
        <VuiButton
          variant="outlined"
          size="medium"
          onClick={handleStay}
          sx={{
            borderColor: "rgba(74,96,128,0.4)",
            color: (t) => t.palette.text.secondary,
            fontWeight: 600,
            "&:hover": { borderColor: "#0a1628", background: "rgba(10,22,40,0.04)" },
          }}
        >
          Continue Editing
        </VuiButton>
        <VuiButton
          size="medium"
          onClick={handleLeave}
          sx={{
            background: "linear-gradient(135deg, #fb8c00, #e65100)",
            color: (t) => t.palette.common.white,
            fontWeight: 700,
            "&:hover": { background: "linear-gradient(135deg, #ffa726, #f57c00)" },
            boxShadow: "0 4px 18px rgba(251,140,0,0.35)",
          }}
        >
          Leave Without Saving
        </VuiButton>
      </DialogActions>
    </Dialog>
  );

  // UnsavedPrompt kept for API compatibility (renders nothing — blocking is done via history.block)
  const UnsavedPrompt = () => null;

  return { ConfirmDialog, UnsavedPrompt, guardClose };
}


